/**
 * 表单业务逻辑工具函数
 * 专门用于 newManual、new、confirm、submit 等页面的公共业务逻辑
 */

import { ElMessage } from 'element-plus';
import { ownerPage } from '../api/owner';
import {
  validateIDCard as validateIDCardUtil,
  validateIdByType
} from './validationUtils';
import { setDisplayName } from './stringUtils';

/**
 * 获取著作权人地址信息的提示文字
 * @param {Object} applicant - 著作权人对象
 * @returns {string} - 提示文字
 * @example
 * getApplicantAddressInfo(applicant) // '请在版权中心填写地址信息'
 */
export const getApplicantAddressInfo = (applicant) => {
  if (!applicant) {
    return '';
  }

  // 如果有登录名，提示去版权中心填写
  if (applicant.loginName) {
    return '请在版权中心填写地址信息';
  }

  // 如果没有登录名，提示授权
  return '请先授权';
};

/**
 * 检查著作权人授权状态并获取信息
 * @param {string} name - 著作权人名称
 * @param {string} type - 类型（'OWN' | 'AGT'）
 * @param {Function} onSuccess - 成功回调
 * @param {Function} onNotFound - 未找到回调
 * @example
 * checkAuth('张三', 'OWN', (data) => {
 *   console.log('找到著作权人:', data)
 * })
 */
export const checkAuth = async (name, type, onSuccess, onNotFound) => {
  if (!name || !name.trim()) {
    return;
  }

  try {
    const params = {
      pageSize: 10,
      pageNum: 1,
      name: name.trim()
    };

    const res = await ownerPage(params);

    if (res.data && res.data.list && res.data.list.length > 0) {
      // 找到匹配的著作权人
      const matchedOwner = res.data.list.find(
        item => item.name === name.trim()
      );

      if (matchedOwner && onSuccess) {
        onSuccess(matchedOwner);
      } else if (!matchedOwner && onNotFound) {
        onNotFound();
      }
    } else {
      // 未找到著作权人
      if (onNotFound) {
        onNotFound();
      }
    }
  } catch (error) {
    console.error('检查授权失败:', error);
    ElMessage.error('检查授权失败，请重试');
  }
};

/**
 * 设置著作权人显示名称（申请人提交场景）
 * @param {Array} applicants - 著作权人数组
 * @param {number} index - 著作权人索引
 * @param {Object} currentApplicant - 当前提交人信息
 * @example
 * setOwnDisplayName(applicants, 0, currentApplicant)
 */
export const setOwnDisplayName = (applicants, index, currentApplicant) => {
  if (!applicants[index] || !applicants[index].name) {
    return;
  }

  const applicant = applicants[index];
  const name = applicant.name;

  if (index === 0) {
    // 第一个著作权人（即申请人自己）
    if (currentApplicant && currentApplicant.loginName) {
      applicant.displayName = `${name}(${currentApplicant.loginName})`;
    } else {
      applicant.displayName = `${name}(未授权)`;
    }
  } else {
    // 其他著作权人
    if (applicant.loginName) {
      applicant.displayName = `${name}(${applicant.loginName})`;
    } else {
      applicant.displayName = name;
    }
  }
};

/**
 * 设置代理人显示名称（代理人提交场景）
 * @param {string} agentName - 代理人名称
 * @param {Object} currentApplicant - 当前提交人信息
 * @returns {string} - 显示名称
 * @example
 * const displayName = setAgtDisplayName('张三', currentApplicant)
 */
export const setAgtDisplayName = (agentName, currentApplicant) => {
  if (!agentName) {
    return '';
  }

  if (currentApplicant && currentApplicant.loginName) {
    return `${agentName}(${currentApplicant.loginName})`;
  } else {
    return `${agentName}(未授权)`;
  }
};

/**
 * 验证著作权人输入的完整性
 * @param {Object} applicant - 著作权人对象
 * @param {number} index - 索引
 * @param {string} applyType - 申请类型
 * @returns {Object} - { valid: boolean, message: string, field: string }
 * @example
 * const result = validateApplicantInput(applicant, 0, 'OWN')
 * if (!result.valid) {
 *   ElMessage.error(result.message)
 * }
 */
export const validateApplicantInput = (applicant, index, applyType = 'OWN') => {
  // 跳过申请人提交时的第一个著作权人（已单独验证）
  if (applyType === 'OWN' && index === 0) {
    return { valid: true };
  }

  // 检查名称
  if (!applicant.name || !applicant.name.trim()) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人名称不能为空`,
      field: 'name'
    };
  }

  // 检查国家
  if (!applicant.country) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人请选择国家`,
      field: 'country'
    };
  }

  // 检查地区
  if (!applicant.areaCode || applicant.areaCode.length === 0) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人请选择地区`,
      field: 'areaCode'
    };
  }

  // 检查人员类型
  if (!applicant.peopleType) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人请选择人员类型`,
      field: 'peopleType'
    };
  }

  // 检查证件类型
  if (!applicant.idType) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人请选择证件类型`,
      field: 'idType'
    };
  }

  // 检查证件号码
  if (!applicant.idNumber) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人请输入证件号码`,
      field: 'idNumber'
    };
  }

  // 验证证件号码格式
  if (!validateIdByType(applicant.idType, applicant.idNumber)) {
    return {
      valid: false,
      message: `第${index + 1}个著作权人证件号码格式不正确`,
      field: 'idNumber'
    };
  }

  return { valid: true };
};

/**
 * 批量验证著作权人列表
 * @param {Array} applicants - 著作权人数组
 * @param {string} applyType - 申请类型
 * @param {string} agentName - 代理人名称（可选）
 * @returns {Object} - { valid: boolean, errors: Array }
 * @example
 * const result = validateApplicantsList(applicants, 'OWN')
 * if (!result.valid) {
 *   result.errors.forEach(err => ElMessage.error(err.message))
 * }
 */
export const validateApplicantsList = (applicants, applyType = 'OWN', agentName = '') => {
  const errors = [];
  const nameSet = new Set();

  if (!applicants || applicants.length === 0) {
    return {
      valid: false,
      errors: [{ message: '请添加著作权人' }]
    };
  }

  applicants.forEach((applicant, index) => {
    const name = applicant.name?.trim();

    // 检查单个著作权人的完整性
    const result = validateApplicantInput(applicant, index, applyType);
    if (!result.valid) {
      errors.push(result);
      return;
    }

    // 跳过第一个著作权人的重复检查（申请人提交场景）
    if (applyType === 'OWN' && index === 0) {
      return;
    }

    // 检查是否与代理人重复
    if (applyType === 'AGT' && agentName && name === agentName.trim()) {
      errors.push({
        valid: false,
        message: `第${index + 1}个著作权人不能与代理人名称重复`,
        field: 'name',
        index
      });
    }

    // 检查著作权人名称是否重复
    if (nameSet.has(name)) {
      errors.push({
        valid: false,
        message: `第${index + 1}个著作权人名称重复`,
        field: 'name',
        index
      });
    } else {
      nameSet.add(name);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * 移动数组元素位置（上移）
 * @param {Array} array - 数组
 * @param {number} index - 当前索引
 * @returns {Array} - 新数组
 * @example
 * const newArray = moveArrayItemUp(oldArray, 2)
 */
export const moveArrayItemUp = (array, index) => {
  if (index <= 0 || index >= array.length) {
    return array;
  }

  const newArray = [...array];
  const temp = newArray[index];
  newArray[index] = newArray[index - 1];
  newArray[index - 1] = temp;

  return newArray;
};

/**
 * 移动数组元素位置（下移）
 * @param {Array} array - 数组
 * @param {number} index - 当前索引
 * @returns {Array} - 新数组
 * @example
 * const newArray = moveArrayItemDown(oldArray, 2)
 */
export const moveArrayItemDown = (array, index) => {
  if (index < 0 || index >= array.length - 1) {
    return array;
  }

  const newArray = [...array];
  const temp = newArray[index];
  newArray[index] = newArray[index + 1];
  newArray[index + 1] = temp;

  return newArray;
};

/**
 * 获取港澳台地区标识
 * @param {Array} areaCode - 地区代码数组
 * @returns {boolean} - 是否为港澳台
 * @example
 * isHMTArea(['810000']) // true (香港)
 * isHMTArea(['110000']) // false (北京)
 */
export const isHMTArea = (areaCode) => {
  if (!areaCode || areaCode.length === 0) {
    return false;
  }

  const HMT_CODES = ['810000', '820000', '710000']; // 香港、澳门、台湾
  return HMT_CODES.includes(areaCode[0]);
};

/**
 * 格式化地区信息为字符串
 * @param {Array} areaCode - 地区代码数组
 * @param {Object} areaOptions - 地区选项映射对象
 * @returns {string} - 地区名称字符串
 * @example
 * formatAreaString(['110000', '110100'], areaOptions)
 * // '北京市-北京市'
 */
export const formatAreaString = (areaCode, areaOptions) => {
  if (!areaCode || areaCode.length === 0 || !areaOptions) {
    return '';
  }

  const names = areaCode.map(code => {
    const option = areaOptions[code];
    return option ? option.label : code;
  });

  return names.join('-');
};
